/**
 * Code samples shown on the developer documentation pages (/docs).
 *
 * Samples are plain strings: no backticks and no "${" inside them, so they survive the
 * TypeScript template literal unchanged. {{BASE}} is replaced with the public API's base URL,
 * {{VERIFY}} with verify-app's. (Until 2026-10-24 a script also generated a C# copy of this file
 * for the Razor portal, which has since been retired.)
 */

/** Public API (api/) base URL. The hosted Swagger UI lives at {API_BASE_URL}/index.html. */
export const API_BASE_URL = 'https://api.inertiaid.com';
export const API_REFERENCE_URL = `${API_BASE_URL}/index.html`;

/** verify-app's host: session links point here, and verify.js is served from it. Sandbox until production ships. */
export const VERIFY_APP_URL = 'https://sandbox.verify.inertiaid.com';

const RAW = {
  // ── Overview ──────────────────────────────────────────────────────────────
  authHeader: String.raw`Authorization: Bearer pk_test_xxxxxxxxxxxxxxxxxxxxxxxx`,

  quickstartCurl: String.raw`curl -X POST {{BASE}}/v1/documents/extract \
  -H "Authorization: Bearer $INERTIAID_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "documentType": "drivers_license",
    "country": "US",
    "frontImageUrl": "https://example.com/samples/license-front.jpg"
  }'`,

  errorExample: String.raw`HTTP/1.1 402 Payment Required
Content-Type: application/problem+json
X-Request-Id: 1-6715a2c4-3b0f9e2d7c1a5e8f4b6d0a92

{
  "status": 402,
  "detail": "Your prepaid credit balance is exhausted. Add credit from your dashboard to resume verifications.",
  "errorCode": "INSUFFICIENT_CREDITS"
}`,

  // ── Document API: extract ─────────────────────────────────────────────────
  extractCurl: String.raw`curl -X POST {{BASE}}/v1/documents/extract \
  -H "Authorization: Bearer $INERTIAID_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "documentType": "drivers_license",
    "country": "US",
    "frontImage": "'"$(base64 -w0 license-front.jpg)"'",
    "backImage": "'"$(base64 -w0 license-back.jpg)"'"
  }'`,

  extractNode: String.raw`import { readFile } from "node:fs/promises";

const b64 = async (path) => (await readFile(path)).toString("base64");

const res = await fetch("{{BASE}}/v1/documents/extract", {
  method: "POST",
  headers: {
    Authorization: "Bearer " + process.env.INERTIAID_API_KEY,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    documentType: "drivers_license",
    country: "US",
    frontImage: await b64("license-front.jpg"),
    backImage: await b64("license-back.jpg"), // optional: PDF417 barcode
  }),
});

if (!res.ok) {
  const problem = await res.json();
  throw new Error(problem.errorCode + ": " + problem.detail);
}

const result = await res.json();
console.log(result.verificationId, result.fields.lastName?.value);`,

  extractPython: String.raw`import base64, os, requests

def b64(path):
    with open(path, "rb") as f:
        return base64.b64encode(f.read()).decode()

resp = requests.post(
    "{{BASE}}/v1/documents/extract",
    headers={"Authorization": "Bearer " + os.environ["INERTIAID_API_KEY"]},
    json={
        "documentType": "drivers_license",
        "country": "US",
        "frontImage": b64("license-front.jpg"),
        "backImage": b64("license-back.jpg"),  # optional: PDF417 barcode
    },
    timeout=30,
)

if not resp.ok:
    problem = resp.json()
    raise RuntimeError(problem["errorCode"] + ": " + problem["detail"])

result = resp.json()
print(result["verificationId"], result["fields"]["lastName"]["value"])`,

  extractCsharp: String.raw`using System.Net.Http.Headers;
using System.Net.Http.Json;

var http = new HttpClient { BaseAddress = new Uri("{{BASE}}") };
http.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", Environment.GetEnvironmentVariable("INERTIAID_API_KEY"));

var response = await http.PostAsJsonAsync("/v1/documents/extract", new
{
    documentType = "drivers_license",
    country      = "US",
    frontImage   = Convert.ToBase64String(await File.ReadAllBytesAsync("license-front.jpg")),
    backImage    = Convert.ToBase64String(await File.ReadAllBytesAsync("license-back.jpg")),
});

var body = await response.Content.ReadAsStringAsync();
if (!response.IsSuccessStatusCode)
    throw new Exception(body); // application/problem+json with an errorCode
Console.WriteLine(body);`,

  extractResponse: String.raw`{
  "environment": "live",
  "data_masked": false,
  "status": "completed",
  "fields": {
    "firstName": {
      "value": "JANE",
      "confidence": 1,
      "sources": [
        { "value": "JANE", "type": "pdf417", "confidence": 1 },
        { "value": "JANE", "type": "ocr", "confidence": 0.9712 }
      ]
    },
    "lastName":       { "value": "SAMPLE",     "confidence": 1, "sources": [ ... ] },
    "dateOfBirth":    { "value": "1990-04-12", "confidence": 1, "sources": [ ... ] },
    "documentNumber": { "value": "S1234567",   "confidence": 1, "sources": [ ... ] },
    "expirationDate": { "value": "2029-04-12", "confidence": 1, "sources": [ ... ] },
    "street":         { "value": "123 MAIN ST", "confidence": 1, "sources": [ ... ] },
    "city":           { "value": "SPRINGFIELD", "confidence": 1, "sources": [ ... ] },
    "state":          { "value": "IL",          "confidence": 1, "sources": [ ... ] },
    "postalCode":     { "value": "62701",       "confidence": 1, "sources": [ ... ] },
    "aamvaVersion": 10
  },
  "confidence": {
    "ocr": 0.9531,
    "barcode": 1,
    "fieldConsistency": 0.98,
    "overall": 0.9712
  },
  "lowQuality": false,
  "processingTimeMs": 2140,
  "verificationId": "3f8c2a9e-6b1d-4f0a-9c7e-2d5b8a1e4f60"
}`,

  maskedFields: String.raw`"firstName":      { "value": "JA*E",  ... },
"lastName":       { "value": "SA***E", ... },
"documentNumber": { "value": "S1***", ... },
"postalCode":     { "value": "***",   ... }
// dateOfBirth, issueDate and expirationDate are removed; MRZ lines are masked the same way`,

  // ── Document API: verify ──────────────────────────────────────────────────
  verifyCurl: String.raw`curl -X POST {{BASE}}/v1/documents/verify \
  -H "Authorization: Bearer $INERTIAID_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "documentType": "passport",
    "frontImageUrl": "https://example.com/uploads/passport.jpg",
    "checkExpiry": true,
    "minConfidenceThreshold": 0.85
  }'`,

  verifyResponse: String.raw`{
  "environment": "live",
  "data_masked": false,
  "status": "completed",
  "fields": { ... },
  "confidence": { "ocr": 0.94, "barcode": 0, "fieldConsistency": 1, "overall": 0.95 },
  "lowQuality": false,
  "processingTimeMs": 1870,
  "verificationId": "8a1e4f60-3f8c-4f0a-9c7e-2d5b6b1d2a9e",
  "verification": {
    "isExpired": false,
    "mrzValid": true,
    "fieldsConsistent": true,
    "fraudSignals": [],
    "declineReasons": [],
    "decision": "approved"
  }
}`,

  verifyRejected: String.raw`"verification": {
  "isExpired": true,
  "mrzValid": true,
  "fieldsConsistent": true,
  "fraudSignals": ["document_expired", "barcode_mismatch"],
  "declineReasons": ["document_expired"],
  "decision": "rejected",
  "fieldMismatches": [
    { "field": "lastName", "ocrValue": "SAMPLE", "barcodeValue": "SAMPLES" }
  ]
}`,

  // ── Document API: verify-age ──────────────────────────────────────────────
  verifyAgeCurl: String.raw`curl -X POST {{BASE}}/v1/documents/verify-age \
  -H "Authorization: Bearer $INERTIAID_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "ageLimit": 21,
    "frontImage": "<base64>",
    "backImage": "<base64>"
  }'`,

  verifyAgeResponse: String.raw`{
  "verificationId": "c7e2d5b8-a1e4-4f60-3f8c-2a9e6b1d4f0a",
  "result": "pass",
  "ageLimit": 21,
  "environment": "live"
}`,

  // ── Document API: retrieval ───────────────────────────────────────────────
  listCurl: String.raw`curl "{{BASE}}/v1/verifications?page=1&pageSize=20" \
  -H "Authorization: Bearer $INERTIAID_API_KEY"`,

  listResponse: String.raw`{
  "total": 132,
  "page": 1,
  "pageSize": 20,
  "items": [
    {
      "verificationId": "3f8c2a9e6b1d4f0a9c7e2d5b8a1e4f60",
      "status": "Approved",
      "documentType": "driverlicense",
      "decision": "approved",
      "overallConfidence": 0.9712,
      "createdAt": "2026-10-20T14:03:11Z",
      "completedAt": "2026-10-20T14:03:13Z"
    }
  ]
}`,

  getCurl: String.raw`curl {{BASE}}/v1/verifications/3f8c2a9e-6b1d-4f0a-9c7e-2d5b8a1e4f60 \
  -H "Authorization: Bearer $INERTIAID_API_KEY"`,

  getResponse: String.raw`{
  "data_masked": false,
  "verificationId": "3f8c2a9e6b1d4f0a9c7e2d5b8a1e4f60",
  "status": "Approved",
  "createdAt": "2026-10-20T14:03:11Z",
  "completedAt": "2026-10-20T14:03:13Z",
  "mode": "id",
  "fields": {
    "firstName":   { "text": "JANE",       "confidence": 0.9712 },
    "lastName":    { "text": "SAMPLE",     "confidence": 0.9655 },
    "dateOfBirth": { "text": "1990-04-12", "confidence": 0.9801 },
    ...
  },
  "ageCheck": null,
  "confidence": { "ocr": 0.9531, "barcode": 1, "fieldConsistency": 0.98, "overall": 0.9712 },
  "verification": {
    "isExpired": false,
    "mrzValid": false,
    "fieldsConsistent": true,
    "fraudSignals": [],
    "declineReasons": [],
    "decision": "approved"
  },
  "documentId": 4821
}`,

  usageCurl: String.raw`curl {{BASE}}/v1/usage \
  -H "Authorization: Bearer $INERTIAID_API_KEY"`,

  usageResponse: String.raw`{
  "clientId": 42,
  "clientName": "Acme Rentals",
  "plan": "Standard",
  "usedThisMonth": 318,
  "resetDate": "2026-11-01",
  "billingModel": "prepaid",
  "creditBalance": 61.40,
  "freeVerificationsRemaining": 0,
  "estimatedVerificationsRemaining": 153,
  "currentRate": 0.40,
  "rates": { "ageCheck": 0.20, "idExtract": 0.35, "idVerification": 0.40, "fullKyc": 0.50 },
  "creditExhausted": false
}`,

  // ── Webhooks ──────────────────────────────────────────────────────────────
  webhookPayload: String.raw`POST /your/webhook HTTP/1.1
Content-Type: application/json
X-InertiaX-Signature: t=1792505000,v1=5f2b9c0e7a...

{
  "event": "verification.decided",
  "createdAt": "2026-10-20T14:03:13Z",
  "verificationId": "3f8c2a9e6b1d4f0a9c7e2d5b8a1e4f60",
  "externalReferenceId": "order_10045",
  "source": "session",
  "mode": "id",
  "ageLimit": null,
  "environment": "live",
  "status": "completed",
  "decision": "approved",
  "reasons": [],
  "scores": { "confidence": 0.91, "faceMatch": 0.82, "liveness": 0.74, "risk": 0.04 },
  "fraudAlerts": { "critical": 0, "high": 0, "medium": 0, "low": 0 },
  "completedAt": "2026-10-20T14:03:13Z",
  "previousDecision": null,
  "changedAt": null
}`,

  webhookNode: String.raw`import express from "express";
import { createHmac, timingSafeEqual } from "node:crypto";

const SECRET = process.env.INERTIAID_WEBHOOK_SECRET; // whsec_...
const TOLERANCE_SECONDS = 300;

function verifySignature(header, rawBody) {
  const parts = Object.fromEntries(
    (header ?? "").split(",").map((p) => p.split("=", 2)),
  );
  const t = Number(parts.t);
  if (!parts.v1 || !Number.isFinite(t)) return false;
  if (Math.abs(Date.now() / 1000 - t) > TOLERANCE_SECONDS) return false;

  const expected = createHmac("sha256", SECRET).update(t + "." + rawBody).digest("hex");
  const a = Buffer.from(expected);
  const b = Buffer.from(parts.v1);
  return a.length === b.length && timingSafeEqual(a, b);
}

const app = express();

// Verify against the RAW body: re-serialised JSON will not match the signature.
app.post("/webhooks/inertiaid", express.raw({ type: "application/json" }), (req, res) => {
  const raw = req.body.toString("utf8");
  if (!verifySignature(req.get("X-InertiaX-Signature"), raw)) return res.sendStatus(401);

  const event = JSON.parse(raw);
  // event.verificationId, event.decision ("approved" | "declined" | "cancelled"), ...
  res.sendStatus(200);
});`,

  webhookPython: String.raw`import hashlib, hmac, os, time
from flask import Flask, request, abort

SECRET = os.environ["INERTIAID_WEBHOOK_SECRET"]  # whsec_...
TOLERANCE_SECONDS = 300
app = Flask(__name__)

def verify_signature(header, raw_body):
    try:
        parts = dict(p.split("=", 1) for p in header.split(","))
        t = int(parts["t"])
        v1 = parts["v1"]
    except (AttributeError, KeyError, ValueError):
        return False
    if abs(time.time() - t) > TOLERANCE_SECONDS:
        return False
    expected = hmac.new(SECRET.encode(), f"{t}.".encode() + raw_body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, v1)

@app.post("/webhooks/inertiaid")
def inertiaid_webhook():
    if not verify_signature(request.headers.get("X-InertiaX-Signature"), request.get_data()):
        abort(401)
    event = request.get_json()
    # event["verificationId"], event["decision"], ...
    return "", 200`,

  webhookCsharp: String.raw`using System.Security.Cryptography;
using System.Text;

static bool VerifySignature(string? header, string rawBody, string secret)
{
    if (header is null) return false;
    var parts = header.Split(',')
        .Select(p => p.Split('=', 2))
        .Where(p => p.Length == 2)
        .ToDictionary(p => p[0], p => p[1]);
    if (!parts.TryGetValue("t", out var t) || !long.TryParse(t, out var ts)) return false;
    if (!parts.TryGetValue("v1", out var v1)) return false;
    if (Math.Abs(DateTimeOffset.UtcNow.ToUnixTimeSeconds() - ts) > 300) return false;

    var expected = Convert.ToHexStringLower(HMACSHA256.HashData(
        Encoding.UTF8.GetBytes(secret), Encoding.UTF8.GetBytes(ts + "." + rawBody)));
    return CryptographicOperations.FixedTimeEquals(
        Encoding.ASCII.GetBytes(expected), Encoding.ASCII.GetBytes(v1));
}

// Minimal API: read the body as a string BEFORE any JSON binding.
app.MapPost("/webhooks/inertiaid", async (HttpRequest req) =>
{
    using var reader = new StreamReader(req.Body);
    var raw = await reader.ReadToEndAsync();
    if (!VerifySignature(req.Headers["X-InertiaX-Signature"], raw, secret))
        return Results.Unauthorized();
    // JsonDocument.Parse(raw) ... act on "decision"
    return Results.Ok();
});`,

  // ── Sessions ──────────────────────────────────────────────────────────────
  createSessionCurl: String.raw`curl -X POST {{BASE}}/v1/sessions \
  -H "Authorization: Bearer $INERTIAID_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "sendSms": false,
    "mode": "id",
    "steps": ["liveness", "id-front", "id-back"],
    "livenessChallenges": ["center", "blink"],
    "externalReferenceId": "order_10045",
    "redirectUrl": "https://shop.example.com/checkout/verified",
    "expiresInMinutes": 30
  }'`,

  createSessionSmsCurl: String.raw`curl -X POST {{BASE}}/v1/sessions \
  -H "Authorization: Bearer $INERTIAID_API_KEY" \
  -H "Content-Type: application/json" \
  -d '{
    "phone": "+1 312 555 0142",
    "merchantName": "Acme Rentals",
    "mode": "age",
    "ageLimit": 21
  }'`,

  createSessionResponse: String.raw`HTTP/1.1 201 Created

{
  "link": "{{VERIFY}}/#/s/9f2c41d0b7e84a6f8e1d3c5a7b9e0f12",
  "sessionToken": "9f2c41d0b7e84a6f8e1d3c5a7b9e0f12",
  "verificationId": "b8a1e4f6-03f8-4c2a-9e6b-1d4f0a9c7e2d",
  "expiresAt": "2026-10-20T14:33:11Z",
  "smsDelivered": false
}`,

  backendNode: String.raw`// Your server. The API key never leaves it.
app.post("/api/verifications", requireLoggedInUser, async (req, res) => {
  const r = await fetch("{{BASE}}/v1/sessions", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + process.env.INERTIAID_API_KEY,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      sendSms: false,
      steps: ["liveness", "id-front", "id-back"],
      livenessChallenges: ["center", "blink"],
      externalReferenceId: req.user.id,
    }),
  });
  if (!r.ok) return res.status(502).json(await r.json());

  const { sessionToken, verificationId } = await r.json();
  await db.users.update(req.user.id, { verificationId }); // to look the result up later
  res.json({ sessionToken }); // only the session token goes to the browser or app
});`,

  embedServerNode: String.raw`// Your server. Same session as for an SDK, but the page gets the link.
app.post("/api/verifications", requireLoggedInUser, async (req, res) => {
  const r = await fetch("{{BASE}}/v1/sessions", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + process.env.INERTIAID_API_KEY,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      sendSms: false,
      steps: ["liveness", "id-front", "id-back", "face-match"],
      livenessChallenges: ["center", "blink"],
      externalReferenceId: req.user.id,
    }),
  });
  if (!r.ok) return res.status(502).json(await r.json());

  const { link, verificationId } = await r.json();
  await db.users.update(req.user.id, { verificationId });
  res.json({ link }); // verify.js opens this link on your page
});`,

  embedUsage: String.raw`<script src="{{VERIFY}}/verify.js"></script>
<div id="idv" style="height: 720px"></div>

<script>
  async function verifyCustomer() {
    const { link } = await (await fetch("/api/verifications", { method: "POST" })).json();

    const result = await InertiaID.verify({
      link,
      container: "#idv",            // inline in this element; omit it for a modal
      onStep: ({ step }) => {},     // intro, capture, submitting, confirm, done...
      onComplete: (result) => {},   // also the promise's value
      onCancel: () => {},           // the customer closed the modal
      onError: ({ code, message, fatal }) => {},
    });
    // result = { verificationId, status: "completed", decision: "approved" }
    //       or { verificationId, status: "cancelled" | "expired", decision: null }

    // A hint for your UI only: let your server read the real decision.
    if (result.status === "completed") {
      await fetch("/api/verifications/confirm", { method: "POST" });
    }
  }
</script>`,

  confirmNode: String.raw`// Your server: the decision you act on.
async function getDecision(verificationId) {
  const r = await fetch("{{BASE}}/v1/verifications/" + verificationId, {
    headers: { Authorization: "Bearer " + process.env.INERTIAID_API_KEY },
  });
  const v = await r.json();
  // v.status: Pending | InProgress | Approved | Declined | Cancelled | Expired
  if (v.status === "Approved") return "approved";
  if (["Declined", "Cancelled", "Expired"].includes(v.status)) return "declined";
  return "pending"; // not finished yet: poll again or wait for the webhook
}`,

  // ── SDKs ──────────────────────────────────────────────────────────────────
  reactInstall: String.raw`npm install @inertiax/kyc-react`,

  reactUsage: String.raw`import { IdVerification } from "@inertiax/kyc-react";

export function VerifyIdentity({ sessionToken }: { sessionToken: string }) {
  return (
    <IdVerification
      sessionToken={sessionToken}
      onComplete={(result) => {
        // result.session = { decision, reasons, redirectUrl }
        // A hint for your UI only: your backend confirms the decision.
        showResult(result.session?.decision);
      }}
      onError={(error) => showError(error.message)}
    />
  );
}`,

  reactFaceMatch: String.raw`// npm install @vladmandic/face-api onnxruntime-web
import { IdVerification, createSFaceMatcher } from "@inertiax/kyc-react";

// SFace matcher for sessions with the "face-match" step. Everything is served
// from your own site: face-api's detector weights (ssd_mobilenetv1 and
// face_landmark_68, about 6 MB), the SFace ONNX model (about 10 MB) and
// onnxruntime-web's .wasm files.
const faceMatcher = createSFaceMatcher({
  faceapi: () => import("@vladmandic/face-api"),
  faceApiModelUrl: "/models/face-api",
  ort: () => import("onnxruntime-web/wasm"),
  sfaceModelUrl: "/models/sface/face_recognition_sface_2021dec_int8bq.onnx",
  wasmPaths: "/models/ort/",
});

<IdVerification sessionToken={sessionToken} faceMatcher={faceMatcher} onComplete={...} />`,

  jsInstall: String.raw`npm install @inertiax/kyc-js`,

  jsUsage: String.raw`<div id="verify"></div>

<script type="module">
  import { KycVerificationUI } from "@inertiax/kyc-js";

  // Ask YOUR backend for a session token (it calls POST /v1/sessions).
  const { sessionToken } = await fetch("/api/verifications", { method: "POST" })
    .then((r) => r.json());

  const ui = new KycVerificationUI("#verify", {
    sessionToken,
    theme: "light",
    onComplete: (result) => {
      // result.session = { decision, reasons, redirectUrl } (a hint)
      location.href = "/verification/done";
    },
    onError: (err) => console.error(err),
  });

  await ui.start();
</script>`,

  jsHeadless: String.raw`import { KycVerification } from "@inertiax/kyc-js";

const kyc = new KycVerification({
  sessionToken,
  onStateChange: (state) => render(state), // guidance text, progress, current step
  onComplete: (result) => {},
  onError: (err) => {},
});

await kyc.start();
// kyc.stop(), kyc.retry(), kyc.destroy()`,

  rnInstall: String.raw`# The SDK, plus the native modules it runs on (installed by your app so they are linked)
npm install @inertiax/kyc-react-native \
  react-native-vision-camera \
  react-native-vision-camera-face-detector \
  react-native-worklets-core \
  react-native-reanimated

cd ios && pod install`,

  rnBabel: String.raw`// babel.config.js: VisionCamera frame processors need both plugins
module.exports = {
  presets: ["module:@react-native/babel-preset"],
  plugins: [
    "react-native-worklets-core/plugin",
    "react-native-reanimated/plugin", // must be last
  ],
};`,

  rnUsage: String.raw`import { IdVerification } from "@inertiax/kyc-react-native";

export function VerifyScreen({ sessionToken, navigation }) {
  return (
    <IdVerification
      sessionToken={sessionToken}
      theme="auto"
      onComplete={(result) => {
        // result.session = { decision, reasons, redirectUrl } (a hint)
        navigation.replace("VerificationDone");
      }}
      onError={(error) => Alert.alert("Verification failed", error.message)}
    />
  );
}`,

  rnFaceMatch: String.raw`// Face match also needs these native modules in your app:
//   npm install onnxruntime-react-native @react-native-ml-kit/face-detection react-native-fs expo-asset
// (expo-file-system works instead of react-native-fs). In a bare React Native app,
// run "npx install-expo-modules@latest" once so expo-asset links.
//
// The SFace ONNX model must be a file on the device. To bundle it with the app,
// let Metro treat .onnx as an asset (metro.config.js):
//   const { getDefaultConfig, mergeConfig } = require("@react-native/metro-config");
//   const defaultConfig = getDefaultConfig(__dirname);
//   module.exports = mergeConfig(defaultConfig, {
//     resolver: { assetExts: [...defaultConfig.resolver.assetExts, "onnx"] },
//   });
// (Expo: expo/metro-config's getDefaultConfig, then push "onnx" onto resolver.assetExts.)
// Or skip bundling: download the model to a path and pass that path as model.
import { Asset } from "expo-asset";
import { IdVerification, createSFaceMatcher } from "@inertiax/kyc-react-native";

const faceMatcher = createSFaceMatcher({
  // A file path, the model's bytes, or a loader that resolves either.
  model: async () => (await Asset.loadAsync(require("./assets/sface.onnx")))[0].localUri!,
});

<IdVerification
  sessionToken={sessionToken}
  livenessOptions={{ faceMatcher }}
  onComplete={...}
/>`,

  rnPermissions: String.raw`<!-- ios/YourApp/Info.plist -->
<key>NSCameraUsageDescription</key>
<string>We use the camera to verify your identity.</string>

<!-- android/app/src/main/AndroidManifest.xml -->
<uses-permission android:name="android.permission.CAMERA" />`,
};

export type SampleKey = keyof typeof RAW;

export const SAMPLES: Record<SampleKey, string> = Object.fromEntries(
  Object.entries(RAW).map(([k, v]) => [k, v.split('{{BASE}}').join(API_BASE_URL).split('{{VERIFY}}').join(VERIFY_APP_URL)]),
) as Record<SampleKey, string>;
